import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import Iconify from '../../../components/iconify';
import { MIconButton } from '../../../components/@mui-extend';
// data
import { CONATCTLIST, telHref, mailHref } from '../../../data/contact-info';
//
import { isActivePath } from './MenuDesktop';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 300;

// Icons are mapped by route so every entry reads correctly, instead of reusing
// the generic navbar bitmaps from the shared menu config.
const NAV_ICONS = {
  '/': 'ic:round-home',
  '/about-us': 'ic:round-info',
  '/achievers': 'ic:round-emoji-events',
  '/blogs': 'ic:round-article',
  '/team': 'ic:round-groups',
  '/contact-us': 'ic:round-email',
};

const ItemStyle = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  minHeight: 48,
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  '& .MuiListItemIcon-root': { minWidth: 36, color: 'inherit' },
  '&::before': {
    content: "''",
    position: 'absolute',
    left: 0,
    top: 10,
    bottom: 10,
    width: 3,
    borderRadius: 2,
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
  },
  '&[aria-current="page"]': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&::before': { backgroundColor: theme.palette.primary.main },
  },
}));

// ----------------------------------------------------------------------

MobileItem.propTypes = {
  item: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
  openTitle: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

function MobileItem({ item, pathname, openTitle, onToggle, onNavigate }) {
  const { title, path, children } = item;
  const isActive = isActivePath(pathname, path);
  const isOpen = openTitle === title;

  if (Array.isArray(children) && children.length) {
    const subItems = children.flatMap((group) => group.items ?? []);

    return (
      <Box>
        <ItemStyle onClick={() => onToggle(title)} aria-expanded={isOpen} sx={isActive ? { color: 'primary.main' } : undefined}>
          <ListItemIcon>
            <Iconify icon={NAV_ICONS[path] ?? 'ic:round-circle'} width={20} />
          </ListItemIcon>
          <ListItemText disableTypography primary={title} sx={{ textTransform: 'capitalize' }} />
          <Iconify
            icon={isOpen ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 18, height: 18, ml: 1 }}
          />
        </ItemStyle>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 2, pb: 1 }}>
            {subItems.map((subItem) => (
              <ListItemButton
                key={subItem.title}
                component={RouterLink}
                to={subItem.path}
                onClick={onNavigate}
                aria-current={pathname === subItem.path ? 'page' : undefined}
                sx={{
                  minHeight: 42,
                  borderRadius: 1,
                  color: 'text.secondary',
                  '&[aria-current="page"]': { color: 'primary.main', fontWeight: 600 },
                }}
              >
                <ListItemText disableTypography primary={subItem.title} />
              </ListItemButton>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <ItemStyle
      component={RouterLink}
      to={path}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
    >
      <ListItemIcon>
        <Iconify icon={NAV_ICONS[path] ?? 'ic:round-circle'} width={20} />
      </ListItemIcon>
      <ListItemText disableTypography primary={title} sx={{ textTransform: 'capitalize' }} />
    </ItemStyle>
  );
}

// ----------------------------------------------------------------------

MenuMobile.propTypes = {
  navConfig: PropTypes.array,
};

export default function MenuMobile({ navConfig = [] }) {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openTitle, setOpenTitle] = useState(null);

  const contact = CONATCTLIST[0];

  useEffect(() => {
    setMobileOpen(false);
    setOpenTitle(null);
  }, [pathname]);

  const handleToggle = (title) => {
    setOpenTitle((current) => (current === title ? null : title));
  };

  return (
    <>
      <MIconButton
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation menu"
        sx={{ ml: 1, color: 'text.primary' }}
      >
        <Iconify icon="eva:menu-2-fill" />
      </MIconButton>

      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        slotProps={{ paper: { sx: { width: DRAWER_WIDTH } } }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 2 }}>
          <Logo sx={{ height: 38 }} />

          <MIconButton onClick={() => setMobileOpen(false)} aria-label="Close navigation menu">
            <Iconify icon="eva:close-fill" />
          </MIconButton>
        </Stack>

        <Divider />

        <Scrollbar>
          <Box sx={{ px: 1.5, py: 2 }}>
            <List disablePadding>
              {navConfig.map((item) => (
                <MobileItem
                  key={item.title}
                  item={item}
                  pathname={pathname}
                  openTitle={openTitle}
                  onToggle={handleToggle}
                  onNavigate={() => setMobileOpen(false)}
                />
              ))}
            </List>

            <Button
              component={RouterLink}
              to="/courses"
              fullWidth
              variant="contained"
              onClick={() => setMobileOpen(false)}
              endIcon={<Iconify icon="eva:arrow-forward-outline" sx={{ width: 18, height: 18 }} />}
              sx={{ mt: 2 }}
            >
              Explore Courses
            </Button>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="overline" sx={{ px: 1, color: 'text.disabled' }}>
              Need help?
            </Typography>

            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Button
                href={telHref(contact.phone)}
                startIcon={<Iconify icon="ic:round-call" sx={{ width: 18, height: 18 }} />}
                sx={{ justifyContent: 'flex-start', color: 'text.secondary', fontWeight: 400 }}
              >
                {contact.phone}
              </Button>

              <Button
                href={mailHref(contact.email)}
                startIcon={<Iconify icon="ic:round-mail-outline" sx={{ width: 18, height: 18 }} />}
                sx={{ justifyContent: 'flex-start', color: 'text.secondary', fontWeight: 400 }}
              >
                {contact.email}
              </Button>
            </Stack>
          </Box>
        </Scrollbar>
      </Drawer>
    </>
  );
}
